import { HttpErrorResponse } from '@angular/common/http';
/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  getDeepFromObject,
  NB_AUTH_OPTIONS,
  NbAuthResult,
  NbAuthService,
  NbAuthSocialLink,
} from '@nebular/auth';
import { Subject } from 'rxjs';
import { AuthTransitionService } from '../../../services/auth-transition.service';
import { TwoFactorService } from '../../../services/two-factor.service';
import { AuthService } from '../../../services/auth.service';
@Component({
  selector: 'nb-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NbLoginComponent {
  redirectDelay: number = 0;
  showMessages: any = {};
  strategy: string = '';

  errors: string[] = [];

  /** Set when sign-in failed because the account was never confirmed. */
  unconfirmedEmail: string | null = null;
  resendLoading = false;
  resendSuccess = false;
  resendError = '';
  messages: string[] = [];
  user: any = {};
  submitted: boolean = false;
  socialLinks: NbAuthSocialLink[] = [];
  rememberMe = false;

  captureToken: string | null = null;

  /** True once /token has answered `2fa_required`: the password was right and
   *  the form is now asking for the code instead. Email and password stay in
   *  memory and are posted again with the code — nobody retypes them. */
  twoFactorRequired = false;
  /** Lets the user fall back to a recovery code when the phone is gone. */
  useRecoveryCode = false;
  /** Proof from leg 1 that the captcha was solved and the password accepted.
   *  Stands in for both on leg 2 and lives ~5 minutes. Kept in memory only —
   *  it is a credential, and losing it on reload is correct. */
  challengeToken: string | null = null;

  renewToken$: Subject<void> = new Subject<void>();

  constructor(
    protected service: NbAuthService,
    @Inject(NB_AUTH_OPTIONS) protected options = {},
    protected cd: ChangeDetectorRef,
    protected router: Router,
    private authTransition: AuthTransitionService,
    private twoFactor: TwoFactorService,
    private authService: AuthService
  ) {
    this.redirectDelay = this.getConfigValue('forms.login.redirectDelay');
    this.showMessages = this.getConfigValue('forms.login.showMessages');
    this.strategy = this.getConfigValue('forms.login.strategy');
    this.socialLinks = this.getConfigValue('forms.login.socialLinks');
    this.rememberMe = this.getConfigValue('forms.login.rememberMe');
  }

  onCaptureTokenReceived(token: string | null): void {
    this.captureToken = token;
  }

  login(): void {
    this.errors = [];
    this.messages = [];
    this.submitted = true;

    if (this.twoFactorRequired && this.challengeToken) {
      // Leg 2: the challenge token replaces password and captcha. No renewal
      // here — that is what made Turnstile run a second time.
      this.user.challenge_token = this.challengeToken;
      this.user.cf_turnstile_response = null;
    } else {
      this.user.challenge_token = null;
      this.user.cf_turnstile_response = this.captureToken;
      // Spent on this attempt. A fresh one is fetched only if we actually
      // stay on this leg — renewing here would make the widget re-verify
      // even when the answer is "now give me your code", where it is never
      // used again.
      this.captureToken = null;
    }
    this.service
      .authenticate(this.strategy, this.user)
      .subscribe((result: NbAuthResult) => {
        this.submitted = false;

        if (result.isSuccess()) {
          this.messages = result.getMessages();
        } else if (this.handleSecondFactor(result)) {
          // A step in the sign-in conversation, not a failure: the form has
          // already moved on to asking for the code.
          this.cd.detectChanges();
          return;
        } else if (this.twoFactorRequired) {
          // Leg 2 failed for something that is not about the code — in
          // practice the five-minute challenge ran out. Without this the user
          // sits on the code screen reading "Incorrect email or password",
          // with no captcha and no password left to retry with, and the only
          // way forward is a "Start over" link they have no reason to click.
          this.cancelSecondFactor();
          this.errors = ['That took too long. Please sign in again.'];
        } else if (this.isUnconfirmed(result)) {
          // Not a wrong password: the registration was never confirmed. The
          // only way forward is a new confirmation mail, so offer it here
          // instead of leaving an English sentence and a dead end.
          this.renewToken$?.next();
          this.unconfirmedEmail = this.user.email ?? null;
          this.errors = [];
        } else {
          this.renewToken$?.next();
          this.errors = result.getErrors();
        }

        const redirect = result.getRedirect();
        if (redirect) {
          // Delegate to AuthTransitionService — it dissolves the auth
          // card, runs the grain-pulse wave, and navigates at the peak.
          // `redirectDelay` is honoured by deferring the trigger; the
          // transition itself contributes its own ~1.1 s of timing.
          setTimeout(() => {
            this.authTransition.start(redirect);
          }, this.redirectDelay);
        }
        this.cd.detectChanges();
      });
  }

  /** True when /token refused because the account is unconfirmed. */
  private isUnconfirmed(result: NbAuthResult): boolean {
    const response = result.getResponse() as any;
    return response?.headers?.get?.('X-Error-Code') === 'account_unconfirmed';
  }

  resendConfirmation(): void {
    if (!this.unconfirmedEmail || this.resendLoading) {
      return;
    }
    this.resendLoading = true;
    this.resendError = '';

    this.authService.resendConfirmation(this.unconfirmedEmail).subscribe({
      next: () => {
        this.resendLoading = false;
        this.resendSuccess = true;
        this.cd.detectChanges();
      },
      error: (err: HttpErrorResponse) => {
        this.resendLoading = false;
        // 429 is the resend cooldown, not a failure. Saying "could not
        // be sent" would tell someone to give up at the exact moment
        // they only have to wait a minute.
        this.resendError =
          err?.status === 429
            ? 'pages.login.unconfirmed.resendCooldown'
            : 'pages.login.unconfirmed.resendError';
        this.cd.detectChanges();
      },
    });
  }

  /** Deal with the three answers /token gives about the second factor.
   *
   *  Returns true when the response was handled here, so the caller knows not
   *  to render it as a login failure -- "your code please" is not an error,
   *  and showing it as one tells the user something went wrong when nothing
   *  did.
   */
  private handleSecondFactor(result: NbAuthResult): boolean {
    const detail = (result.getResponse() as any)?.error?.detail;
    const code = detail && typeof detail === 'object' ? detail.code : null;

    if (code === '2fa_required') {
      // Leg 1 is done: captcha solved, password accepted. The challenge token
      // stands in for both on leg 2, so the captcha is NOT renewed here --
      // Turnstile tokens are single use, and renewing would send the user
      // through the widget a second time and keep the button disabled while
      // it re-verifies.
      this.twoFactorRequired = true;
      this.challengeToken = detail.challenge_token || null;
      if (this.challengeToken) {
        // Not needed again, so do not keep it lying around. Without a token
        // (older backend) leg 2 still needs it, and login() falls back to the
        // credential route — one extra captcha, but it works.
        this.user.password = '';
      }
      this.errors = [];
      return true;
    }

    if (code === '2fa_invalid') {
      this.twoFactorRequired = true;
      // The server hands back a fresh one, so a typo does not also run the
      // five-minute window out.
      this.challengeToken = detail.challenge_token || this.challengeToken;
      this.user.totp_code = '';
      this.user.recovery_code = '';
      this.errors = [
        this.useRecoveryCode
          ? 'That recovery code was not accepted. Each code works only once.'
          : 'That code was not accepted. Use the one your app shows right now.',
      ];
      return true;
    }


    if (code === 'too_many_attempts' || code === 'too_many_code_attempts') {
      // 429: the server stopped counting our guesses. Say how long, because
      // "try again later" with no number reads like a broken login.
      const minutes = Math.max(1, Math.ceil((detail.retry_after || 60) / 60));
      this.errors = [
        code === 'too_many_code_attempts'
          ? `Too many wrong codes. Wait ${minutes} minute(s), or use a recovery code.`
          : `Too many attempts. Wait ${minutes} minute(s) and try again.`,
      ];
      // Stay on whichever step we were on; only the waiting changed. On
      // leg 2 there is no captcha in play, so there is nothing to renew.
      if (!this.twoFactorRequired) {
        this.renewToken$?.next();
      }
      return true;
    }

    if (code === '2fa_enrollment_required') {
      // Password correct, but this organisation requires a second factor and
      // this account has none yet. The enrolment token is the only thing that
      // opens the setup routes, and it carries no role claim.
      this.twoFactor.beginForcedEnrollment(detail.enrollment_token, detail.reason || null);
      this.router.navigateByUrl('/auth/two-factor-setup');
      return true;
    }

    return false;
  }

  /** Switch between authenticator code and recovery code. */
  toggleRecoveryCode(): void {
    this.useRecoveryCode = !this.useRecoveryCode;
    this.user.totp_code = '';
    this.user.recovery_code = '';
    this.errors = [];
  }

  /** Back to email + password. */
  cancelSecondFactor(): void {
    this.challengeToken = null;
    this.twoFactorRequired = false;
    this.useRecoveryCode = false;
    this.user.totp_code = '';
    this.user.recovery_code = '';
    this.user.password = '';
    this.errors = [];
  }


  getConfigValue(key: string): any {
    return getDeepFromObject(this.options, key, null);
  }
}
